import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { MoreVert, Edit, Delete, Send } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { commentService, getImageUrl } from '../../services/api';

interface Comment {
  _id: string;
  content: string;
  catId: string;
  authorId: {
    _id: string;
    username: string;
    profilePicture: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface CommentsProps {
  catId: string;
}

const Comments: React.FC<CommentsProps> = ({ catId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);

  useEffect(() => {
    fetchComments();
  }, [catId]);

  const fetchComments = async () => {
    try {
      const data = await commentService.getByCatId(catId);
      setComments(data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const comment = await commentService.create(catId, newComment);
      setComments([comment, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to create comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editingId || !editContent.trim()) return;
    try {
      const updated = await commentService.update(editingId, editContent);
      setComments(comments.map((c) => (c._id === editingId ? updated : c)));
      setEditingId(null);
      setEditContent('');
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await commentService.delete(id);
      setComments(comments.filter((c) => c._id !== id));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
    setMenuAnchor(null);
  };

  const canModify = (comment: Comment) =>
    user && (user.isAdmin || user._id === comment.authorId._id);

  const formatDate = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes <= 1 ? 'Just now' : `${minutes}m ago`;
      }
      return `${hours}h ago`;
    }
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Comments ({comments.length})
      </Typography>

      {/* New Comment Form */}
      {user ? (
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Avatar
            src={getImageUrl(user.profilePicture)}
            sx={{ width: 40, height: 40 }}
          >
            {user.username[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 1 }}
            />
            <Button
              variant="contained"
              size="small"
              endIcon={<Send />}
              onClick={handleSubmit}
              disabled={!newComment.trim() || submitting}
            >
              {submitting ? 'Posting...' : 'Post'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.03)',
            textAlign: 'center',
          }}
        >
          <Typography color="text.secondary">
            Please log in to leave a comment
          </Typography>
        </Box>
      )}

      {/* Comments List */}
      {comments.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {comments.map((comment) => (
            <Box
              key={comment._id}
              sx={{
                display: 'flex',
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <Avatar
                src={getImageUrl(comment.authorId.profilePicture)}
                sx={{ width: 40, height: 40 }}
              >
                {comment.authorId.username[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {comment.authorId.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(comment.createdAt)}
                    {comment.updatedAt !== comment.createdAt && ' (edited)'}
                  </Typography>
                </Box>

                {editingId === comment._id ? (
                  <Box>
                    <TextField
                      fullWidth
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" onClick={handleEdit}>
                        Save
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingId(null);
                          setEditContent('');
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}
                  >
                    {comment.content}
                  </Typography>
                )}
              </Box>

              {canModify(comment) && editingId !== comment._id && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    setSelectedComment(comment);
                    setMenuAnchor(e.currentTarget);
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Comment Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (selectedComment) {
              setEditingId(selectedComment._id);
              setEditContent(selectedComment.content);
            }
            setMenuAnchor(null);
          }}
        >
          <Edit sx={{ mr: 1, fontSize: 18 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => selectedComment && handleDelete(selectedComment._id)}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1, fontSize: 18 }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Comments;
